import type { UniverseId } from '@/domain/universes/universe.interface';
import type { EntityState } from '@ngrx/entity';
import type { Observable } from 'rxjs';
import type { BuildingId, NormalizedPeriod, Period } from './Building.interface';
import type { SensorIdentifier } from './Sensor.interface';
import type { SystemId } from './System.interface';
import type { SensorTimeseriesId } from './Timeseries.interface';
import type { TypicalWeekPeriodApiFormat } from './TypicalWeeks.interface';
import type { UnitCode } from './Unit.interface';

export interface TileData {
    tileId: string;
    name: string;
    tileType: string;
    tileGroup: string;
    buildingId: BuildingId;
    description: any;
    value: any;
    error: any;
}

export interface TypedTileData<DescriptionType, ValueType, ErrorType> extends TileData {
    description: DescriptionType;
    value: ValueType;
    error: ErrorType;
}

export type MultiTileDescriptionData = MultiTileDescription<void>;
export type MultiStateTileDescriptionData = MultiTileDescription<MultiStateTileSensorDisplayParameters>;

export type IpeTileData = TypedTileData<IpeTileDescription, number, string>;
export type IpeHistoryTileData = TypedTileData<IpeTileDescription, TileRecord[], string>; // Not used for a SpawnableTile but ipe tiles history uses the same tile API endpoint
export type MultiTileData = TypedTileData<MultiTileDescriptionData, MultiTileValue[], string>;
export type AnalysisGraphTileData = TypedTileData<AnalysisGraphTileDescription, void, string>;
export type MultiStateSensorsTileData = TypedTileData<MultiStateTileDescriptionData, MultiTileValue[], string>;
export type TypicalWeeksTileData = TypedTileData<TypicalWeeksTileDescription, void, string>;
export type ComfortTileData = TypedTileData<ComfortTileDescription, number, string>;

export interface TileConfig {
    period?: NormalizedPeriod;
    hasTileAdminAccess$?: Observable<boolean>;
}

///// IPE TILES ////

export const defaultIpeTile: IpeTileData = {
    tileId: '',
    buildingId: '',
    tileGroup: '',
    name: '',
    tileType: '',
    value: 0,
    error: null!,
    description: {
        universeIds: [],
        unit: null!,
        customIndicator: {
            label: '',
            value: 1
        },
        energyType: '',
        indicator: '',
        ranges: [],
        yearsRanges: false,
        referenceYear: null!,
        targetYear: null!,
        targetYearRatio: null!
    }
};

export enum IpeTileUnitCode {
    KGEQCO2 = 'kg',
    EUR = 'euro',
    M3 = 'm3',
    KWH = 'kwh'
}

export interface IpeTileCustomIndicator {
    label: string;
    value: number;
}

export interface TileRange {
    start: number;
    end: number;
    color: string;
}

export interface StepTile {
    title: string;
    titleEdit: string;
    percentage: number;
    options: any[];
}

export interface TileRecord {
    period: string;
    value: number;
    error: string;
}

///// CUSTOM TILES ////
export interface CustomTileType {
    label: string;
    value: string;
    icon: string;
    page: string;
    disabled: boolean;
}

export interface CustomTileModel {
    id: string;
    name: string;
    buildingId: BuildingId;
    type: string;
}

export interface MultiTileModel extends CustomTileModel {
    sensors: MultiTileSensor<any>[];
}

export enum LegacyGraphTileTimeStep {
    HOUR = '',
    DAY = '1d',
    MONTH = '1m'
}

export interface LegacyGraphTileSensor {
    sensorId: string;
    systemId: SystemId;
    timeseriesId: SensorTimeseriesId;
    universeId: UniverseId;
    colorHex: string;
    previousPeriod: boolean;
    previousPeriodColorHex: string;
    displayUnitCode: UnitCode | null;
    plotType: string;
    groupId: number | null;
}

export interface LegacyGraphTileTemplate {
    templateId: string | null;
    templateName: string | null;
    buildingId: BuildingId | null;
    sensors: EntityState<LegacyGraphTileSensor>;
    weather: boolean;
    previousWeather: boolean;
    axisEnabled: boolean;
    timeStep: LegacyGraphTileTimeStep;
    periods: {
        current: Period;
        previous: Period;
    };
    sensorGroups: {
        groupId: number;
        labelToTranslate: string;
        unitCode: UnitCode | null;
    }[];
}

///// DEFAULT CUSTOM TILES ////

export const defaultCustomTileModel: CustomTileModel = {
    id: null!,
    name: null!,
    buildingId: null!,
    type: null!
};

export const defaultMultiTileModel: MultiTileModel = {
    ...defaultCustomTileModel,
    sensors: []
};

///// MULTI STATES TILES ////

export interface MultiStateTileSensorDisplayParameters {
    lowValueColorHex: string;
    lowValueLabel: string;
    highValueColorHex: string;
    highValueLabel: string;
}

///// MULTI TILES ////

export interface MultiTileSensor<DisplayType> {
    universeId: UniverseId;
    sensorIdentifier: SensorIdentifier;
    timeseriesId: SensorTimeseriesId;
    displayParameters: DisplayType;
    min: number;
    max: number;
    trackMin: number;
    trackMax: number;
}

export interface MultiTileValue {
    sensorIdentifier: SensorIdentifier;
    value: number;
    timestamp: number;
}

///// COMFORT TILES ////

export interface ComfortTileSensor {
    universeId: UniverseId;
    timeseriesId: SensorTimeseriesId;
    min: number;
    max: number;
}

////// TILE CREATION BODY ///////

export interface TileCreationBody {
    buildingId: BuildingId;
    tileGroup: string;
    name: string;
    type: string;
}

export interface MultiSensorTileCreationBody extends TileCreationBody {
    sensors: MultiTileSensor<void>[];
}

////// TILE DESCRIPTION ///////

export interface IpeTileDescription {
    energyType: string;
    indicator: string;
    customIndicator: IpeTileCustomIndicator;
    ranges: TileRange[];
    unit: IpeTileUnitCode;
    universeIds: UniverseId[];
    yearsRanges: boolean;
    referenceYear: number;
    targetYear: number;
    targetYearRatio: number;
}

export interface AnalysisGraphTileDescription {
    areAxesActive: boolean;
    displayParameters: any;
    startDate: string;
    endDate: string;
    previousPeriodStartDate: string;
    timeStep: string;
    isWeatherActive: boolean;
    isWeatherPreviousPeriodActive: boolean;
    periodCode: string;
    sensors: AnalysisGraphTileDescriptionSensor[];
}

export interface AnalysisGraphTileDescriptionSensor {
    sensorId: string;
    systemId: SystemId;
    timeseriesId: SensorTimeseriesId;
    universeId: UniverseId;
    plotColorHex: string;
    plotPreviousPeriodColorHex: string;
    isPreviousPeriodActive: boolean;
    displayUnitCode: UnitCode;
    plotType: string;
    groupId: number;
}

export interface MultiTileDescription<SensorDisplayParametersType> {
    sensors: MultiTileSensor<SensorDisplayParametersType>[];
}

export interface TypicalWeeksTileDescription {
    timeseriesId: SensorTimeseriesId;
    periods: TypicalWeekPeriodApiFormat[];
}

export interface ComfortTileDescription {
    sensors: ComfortTileSensor[];
}
